import {
  databases,
  DATABASE_ID,
  COLLECTION_ID_CAR_RENTALS
} from '../config/appwrite';
import { ID, Query } from 'appwrite';
import { scheduleFleetPoSyncFromCarRental } from './fleetPoCarRentalSync';

const STATUS_VALUES = ['Paid', 'Processed', 'Pending', 'Waiting for PO'];

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const n = Number(String(value).replace(/,/g, '').trim());
  return Number.isFinite(n) ? n : 0;
};

const toInt = (value, fallback = 0) => {
  const n = parseInt(String(value), 10);
  return Number.isFinite(n) ? n : fallback;
};

/** Map Appwrite document → row shape used by the page. */
export const formatCarRentalRow = (doc) => ({
  id: doc.$id ?? doc.id,
  project: doc.project ?? '',
  userName: doc.userName ?? '',
  regNumber: doc.regNumber ?? '',
  rentalCompany: doc.rentalCompany ?? '',
  amount: toNumber(doc.amount),
  poNumber: doc.poNumber ?? '',
  invoiceNumber: doc.invoiceNumber ?? '',
  status: STATUS_VALUES.includes(doc.status) ? doc.status : 'Pending',
  datePaid: doc.datePaid ?? '',
  comment: doc.comment ?? '',
  month: toInt(doc.month, 1),
  year: toInt(doc.year, new Date().getFullYear())
});

const toPayload = (fields) => {
  const amount = toNumber(fields.amount);
  return {
    project: String(fields.project ?? '').trim(),
    userName: String(fields.userName ?? '').trim(),
    regNumber: String(fields.regNumber ?? '').trim(),
    rentalCompany: String(fields.rentalCompany ?? '').trim(),
    amount: Number.isFinite(amount) ? amount : 0,
    poNumber: String(fields.poNumber ?? '').trim(),
    invoiceNumber: String(fields.invoiceNumber ?? '').trim(),
    status: STATUS_VALUES.includes(fields.status) ? fields.status : 'Pending',
    datePaid: String(fields.datePaid ?? '').trim(),
    comment: String(fields.comment ?? '').trim(),
    month: toInt(fields.month, 1),
    year: toInt(fields.year, new Date().getFullYear())
  };
};

/** Human-readable message from Appwrite SDK errors (browser or Node). */
export const getCarRentalErrorMessage = (error) => {
  if (!error) return 'Unknown error';
  const msg = error.message || error.response?.message;
  if (typeof msg === 'string' && msg.trim()) return msg.trim();
  if (error.code) return `Error ${error.code}`;
  return 'Request failed';
};

/**
 * List all car-rental invoice rows (paginated). Prefer this on the page so month/year
 * switches are instant client-side filters instead of one round-trip per period.
 */
export const fetchAllCarRentals = async () => {
  const all = [];
  let cursor = null;
  const limit = 500;

  for (let page = 0; page < 20; page += 1) {
    const queries = [Query.limit(limit)];
    if (cursor) queries.push(Query.cursorAfter(cursor));

    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_CAR_RENTALS,
      queries
    );
    all.push(...response.documents.map(formatCarRentalRow));
    if (response.documents.length < limit) break;
    cursor = response.documents[response.documents.length - 1].$id;
  }

  all.sort((a, b) => {
    const y = (a.year || 0) - (b.year || 0);
    if (y !== 0) return y;
    const mo = (a.month || 0) - (b.month || 0);
    if (mo !== 0) return mo;
    const p = String(a.project || '').localeCompare(String(b.project || ''), undefined, {
      sensitivity: 'base'
    });
    if (p !== 0) return p;
    return String(a.regNumber || '').localeCompare(String(b.regNumber || ''), undefined, {
      sensitivity: 'base'
    });
  });

  return all;
};

const SESSION_CACHE_KEY = 'striitsmart:carRentalRows:v1';
const SESSION_CACHE_TTL_MS = 10 * 60 * 1000;

/** Read cached rows from sessionStorage for instant first paint (may be stale). */
export const readCarRentalsSessionCache = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rows || !Array.isArray(parsed.rows)) return null;
    if (Date.now() - (parsed.at || 0) > SESSION_CACHE_TTL_MS) return null;
    return parsed.rows.map((row) => formatCarRentalRow(row));
  } catch {
    return null;
  }
};

export const writeCarRentalsSessionCache = (rows) => {
  try {
    sessionStorage.setItem(
      SESSION_CACHE_KEY,
      JSON.stringify({ at: Date.now(), rows: rows || [] })
    );
  } catch {
    /* quota / private mode */
  }
};

/**
 * List all car-rental invoice rows for a calendar month/year.
 * Uses the year+month index only (no sort on project — that was slow without an index).
 */
export const fetchCarRentalsByPeriod = async (year, month) => {
  const y = toInt(year, new Date().getFullYear());
  const m = toInt(month, 1);
  const all = [];
  let cursor = null;
  const limit = 500;

  for (let page = 0; page < 20; page += 1) {
    const queries = [
      Query.equal('year', y),
      Query.equal('month', m),
      Query.limit(limit)
    ];
    if (cursor) queries.push(Query.cursorAfter(cursor));

    const response = await databases.listDocuments(
      DATABASE_ID,
      COLLECTION_ID_CAR_RENTALS,
      queries
    );
    all.push(...response.documents.map(formatCarRentalRow));
    if (response.documents.length < limit) break;
    cursor = response.documents[response.documents.length - 1].$id;
  }

  all.sort((a, b) => {
    const p = String(a.project || '').localeCompare(String(b.project || ''), undefined, {
      sensitivity: 'base'
    });
    if (p !== 0) return p;
    return String(a.regNumber || '').localeCompare(String(b.regNumber || ''), undefined, {
      sensitivity: 'base'
    });
  });

  return all;
};

/**
 * Create a new row document. Returns the formatted row.
 */
export const createCarRentalRow = async (fields) => {
  try {
    const doc = await databases.createDocument(
      DATABASE_ID,
      COLLECTION_ID_CAR_RENTALS,
      ID.unique(),
      toPayload(fields)
    );
    const row = formatCarRentalRow(doc);
    scheduleFleetPoSyncFromCarRental(row.poNumber);
    return row;
  } catch (error) {
    console.error('createCarRentalRow failed:', error);
    throw error;
  }
};

/**
 * Update an existing row (full document fields).
 */
export const upsertCarRentalRow = async (id, fields, options = {}) => {
  if (!id) return null;
  try {
    const previousPo = String(options.previousPoNumber || '').trim();
    const doc = await databases.updateDocument(
      DATABASE_ID,
      COLLECTION_ID_CAR_RENTALS,
      id,
      toPayload(fields)
    );
    const row = formatCarRentalRow(doc);
    scheduleFleetPoSyncFromCarRental(row.poNumber);
    if (previousPo && previousPo !== String(row.poNumber || '').trim()) {
      scheduleFleetPoSyncFromCarRental(previousPo);
    }
    return row;
  } catch (error) {
    console.error('upsertCarRentalRow failed:', id, error);
    throw error;
  }
};

export const deleteCarRentalRow = async (id) => {
  if (!id) return;
  await databases.deleteDocument(DATABASE_ID, COLLECTION_ID_CAR_RENTALS, id);
};

export { STATUS_VALUES };
